import React, {useState} from "react";
import {
    CommandBar,
    IColumn,
    ICommandBarItemProps,
    Icon, Selection,
    SelectionMode,
    ShimmeredDetailsList
} from "@fluentui/react";
import {ITaxCode} from "../../models/TaxCode";
import {useDeleteTaxCodeMutation, useGetTaxCodesQuery} from "../../store/Api";
import {ColumnGenerator, useConditionalButtonColumn} from "../../logic/Columns/ColumnGenerator";
import useCan, {TAX_CODE_MODIFY} from "../../permissions/ability"
import EditTaxCodeModal from "../../components/TaxCodeModals/EditTaxCodeModal";
import DeleteTaxCodeModal from "../../components/TaxCodeModals/DeleteTaxCodeModal";
import { useUrlPanelState } from '../../hooks/useUrlState';


const TaxCodeManagement: React.FC = () => {
    const getTaxCodes = useGetTaxCodesQuery();
    /*
     * Editing an existing record goes through the URL (?panel=taxCode&instance=<id>) so a link
     * can open it; the add flow has nothing to link to, so it stays local.
     */
    const taxCodePanel = useUrlPanelState('taxCode');
    const [creating, setCreating] = useState(false);
    const selectedTaxCode = creating ? undefined : getTaxCodes.data?.find((t) => (t.id ?? t.name) === taxCodePanel.instanceId);
    const modalOpen = taxCodePanel.isOpen && (creating || !!selectedTaxCode);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedTaxCodes, setSelectedTaxCodes] = useState<ITaxCode[] | undefined>()
    const commands: ICommandBarItemProps[] = [
        {
            key: 'AddTaxCode',
            text: 'Add',
            iconProps: { iconName: 'Add' },
            onClick: () => {
                setCreating(true);
                taxCodePanel.open();
            }
        }
    ]

    const farCommands: ICommandBarItemProps[] = [
        {
            key: 'DeleteSelectedTaxCodes',
            text: selectedTaxCodes ? `Delete Selected ${selectedTaxCodes.length} Tax Codes` : 'No Tax Codes Selected',
            disabled: !selectedTaxCodes || selectedTaxCodes?.length === 0,
            iconProps: { iconName: 'Delete' },
            onClick: () => {
                setDeleteModalOpen(true);
            }
        }
    ]

    const selection = new Selection({
        onSelectionChanged: () => {
            const selected = selection.getSelection() as ITaxCode[];
            setSelectedTaxCodes([...selected])
        },
    })

    const columns: IColumn[] = [
        ColumnGenerator('Name','name', 100, 300),
        ColumnGenerator('Rate','rate', 100, 300),
        ColumnGenerator('Description', 'description', 100),
        ColumnGenerator('Active', 'active', 50, 50, (c: ITaxCode) => c.active
             ? <span style={{fontSize:'2em', fontWeight: 'bold'}}><Icon iconName='CheckMark' /></span> : <></>
        ),
        useConditionalButtonColumn((c: ITaxCode) => {setCreating(false); taxCodePanel.open(c.id ?? c.name);}, 'Edit', 'Edit', TAX_CODE_MODIFY)
    ]

    const canModify = useCan(TAX_CODE_MODIFY);

    return (
        <div className="main-content">
            <h1>Tax Code Management</h1>
            {canModify && (
            <CommandBar items={commands} farItems={farCommands} />
            )}
            <ShimmeredDetailsList items={getTaxCodes.data ?? []} enableShimmer={getTaxCodes.isFetching} columns={columns} selectionMode={SelectionMode.multiple} selection={selection} />
            <DeleteTaxCodeModal displayModal={deleteModalOpen} closer={() => setDeleteModalOpen(false)} selectedTaxCodes={selectedTaxCodes}></DeleteTaxCodeModal>
            <EditTaxCodeModal
                taxCode={selectedTaxCode}
                isOpen={modalOpen}
                onClose={() => {
                    setCreating(false);
                    taxCodePanel.close()
                }}
            />
        </div>
    );
}

export default TaxCodeManagement;
